/**
 * The ControlState stream, recorded alongside the audio (architecture.md §4).
 *
 * JSON-lines: a header, then one line per frame, plus an event line whenever
 * the experiment config changes mid-take. Costs almost nothing (~30 lines/s)
 * and is the method for the Gate 8 decision — a take replayed through the
 * mappers under each cutoff-source arm is an A/B of the SAME performance,
 * instead of three performances compared through the memory of how the first
 * one felt. It also makes a take re-renderable and convertible later without
 * re-performing it.
 *
 * The header records which experiments were active, because a re-render that
 * does not know which arm produced the take cannot interpret it.
 */

import type { ControlState, FeatureFrame } from '../mapping/types';
import { type ExperimentConfig, getExperiments, onExperimentsChange } from '../mapping/config';
import { DEFAULT_VOCAB } from '../mapping/vocabulary';

export interface ControlLog {
  start(sceneId: string): void;
  /** Ignored unless started — push unconditionally from the frame route. */
  push(frame: FeatureFrame, control: ControlState): void;
  /** Ends the take. Null if nothing was recorded. */
  stop(): Blob | null;
  readonly recording: boolean;
}

const round = (v: number): number => Math.round(v * 10000) / 10000;

const packFeatures = (f: FeatureFrame['expr']): Record<string, unknown> => ({
  a: round(f.aperture), h: round(f.height), r: round(f.roll), s: round(f.spread),
  g: round(f.angle), f: f.fistPose ? 1 : 0, p: f.present ? 1 : 0, c: f.confident ? 1 : 0,
});

export function createControlLog(): ControlLog {
  let lines: string[] | null = null;
  let t0 = 0;
  let unsub: (() => void) | null = null;

  const now = (): number => Math.round(performance.now() - t0);

  return {
    get recording() { return lines !== null; },

    start(sceneId) {
      t0 = performance.now();
      lines = [JSON.stringify({
        schema: 'controllog/1',
        startedAt: new Date().toISOString(),
        sceneId,
        experiments: getExperiments(),
        vocab: DEFAULT_VOCAB,
        fields: {
          t: 'ms since take start',
          e: 'expression hand: a=aperture h=height r=roll s=spread g=angle f=fistPose p=present c=confident',
          p: 'pitch hand, same keys; absent when not tracked',
          c: 'ControlState: d=degree o=octave l=latched f=fist n=pinch triggers as [kind,index,velocity]',
          event: 'config = experiments changed mid-take; the new values apply from this line on',
        },
      })];
      unsub = onExperimentsChange((cfg: ExperimentConfig) => {
        lines?.push(JSON.stringify({ t: now(), event: 'config', experiments: cfg }));
      });
    },

    push(frame, control) {
      if (!lines) return;
      const line: Record<string, unknown> = {
        t: now(),
        e: packFeatures(frame.expr),
        c: {
          d: control.degree, o: control.octave,
          l: control.latched ? 1 : 0, f: control.fist ? 1 : 0, n: control.pinch ? 1 : 0,
          ...(control.triggers.length
            ? { trig: control.triggers.map((tr) => [tr.kind, tr.index, tr.velocity]) }
            : {}),
        },
      };
      if (frame.pitch) line.p = packFeatures(frame.pitch);
      if (control.panic) line.panic = 1;
      lines.push(JSON.stringify(line));
    },

    stop() {
      unsub?.();
      unsub = null;
      const done = lines;
      lines = null;
      if (!done || done.length <= 1) return null;
      return new Blob([done.join('\n') + '\n'], { type: 'application/jsonl' });
    },
  };
}
